import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Checkbox,
  Chip,
  Fab,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { AppContext } from "../../AppContextProvider";
import { Add, Clear } from "@mui/icons-material";
import Rfq from "../../models/Rfq";
import { green, grey, lightBlue } from "@mui/material/colors";
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
  useGridApiRef,
} from "@mui/x-data-grid";
import CreateRfqDialog from "./CreateRfqDialog";
import CloseRfqSplitButton from "./CloseRfqSplitButton";
import RfqPriorityCell from "./RfqPriorityCell";
import RfqNoteCell from "./RfqNoteCell";
import RfqStatusCell from "./RfqStatusCell";
import RfqAssignedToCell from "./RfqAssignedToCell";
import {
  PO_REVIEW_URL,
  PRICE_QUOTE_REVIEW_URL,
  QUEUE_URL,
  RFQ_URL,
} from "../../MainAppPanel";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import WorkQueueTabs from "../WorkQueueTabs";
import { DateTimeUtils } from "../../DateTimeUtils";
import { getRFQStatusLabel, RFQStatus } from "../../models/RFQStatus";
import {
  getPriceQuotes,
  getQuote,
  searchRfqs,
  updateRfq,
} from "../../api/ProcurementClient";
import PriceQuote from "../../models/PriceQuote";
import { useWorkQueuePurchaseOrders } from "../../hooks/useWorkQueuePurchaseOrders";
import {
  latestReceivedPriceQuote,
  openPriceQuoteReviewInNewTab,
  RECEIVED_PRICE_QUOTE_LINE_HIGHLIGHT,
  subscribePriceQuotesChanged,
  workQueuePriceQuoteStatus,
  WorkQueuePriceQuoteStatus,
} from "../../utils/priceQuoteWorkQueue";
import {
  getLastWorkQueueRfqId,
  latestDraftInboundPoByRfqId,
  openPurchaseOrderReviewInNewTab,
  poNumbersByRfqId,
  rememberLastWorkQueueRfq,
  vendorOrderRfqIdSet,
} from "../../utils/purchaseOrderWorkQueue";
import WorkQueueCalendar, {
  WorkQueueCalendarDateField,
  WorkQueueDateSortToggle,
  WorkQueueViewToggle,
} from "./WorkQueueCalendar";

const SEARCH_DEBOUNCE_MS = 300;
const SERVER_SEARCH_LIMIT = 25;
/** RFQs outside the loaded work-queue list are fetched one by one; keep it small. */
const MAX_FETCHED_SEARCH_RFQS = 10;

function priceQuoteListFingerprint(quotes: PriceQuote[]): string {
  return quotes.map((q) => `${q.id}:${q.status}:${q.rfqId ?? ""}`).join("|");
}

/** Chronological compare for grid date columns (empty dates sort last). */
function compareGridDateStrings(a: unknown, b: unknown): number {
  const sa = typeof a === "string" ? a.trim() : "";
  const sb = typeof b === "string" ? b.trim() : "";
  if (!sa && !sb) return 0;
  if (!sa) return 1;
  if (!sb) return -1;
  return DateTimeUtils.toDate(sa).getTime() - DateTimeUtils.toDate(sb).getTime();
}

/**
 * Date sort that keeps pending inbound-PO reviews pinned above other rows
 * (including archived RFQs resurfaced for PO comparison).
 * DataGrid negates comparator results for `desc`, so PO priority must
 * compensate for the active sort direction or those rows sink to the bottom.
 */
function compareGridDateStringsPreferInboundPo(
  v1: unknown,
  v2: unknown,
  cellParams1: {
    id: string | number;
    field: string;
    api: {
      getRow: (id: string | number) => { hasInboundPo?: boolean } | null;
      getSortModel?: () => GridSortModel;
    };
  },
  cellParams2: {
    id: string | number;
    field: string;
    api: {
      getRow: (id: string | number) => { hasInboundPo?: boolean } | null;
      getSortModel?: () => GridSortModel;
    };
  }
): number {
  const row1 = cellParams1.api.getRow(cellParams1.id);
  const row2 = cellParams2.api.getRow(cellParams2.id);
  const po1 = Boolean(row1?.hasInboundPo);
  const po2 = Boolean(row2?.hasInboundPo);
  if (po1 !== po2) {
    const sortModel = cellParams1.api.getSortModel?.() ?? [];
    const sortItem =
      sortModel.find((s) => s.field === cellParams1.field) ?? sortModel[0];
    const isDesc = sortItem?.sort === "desc";
    // Asc: PO first → negative when po1. Desc: DataGrid negates, so flip.
    if (po1 && !po2) return isDesc ? 1 : -1;
    return isDesc ? -1 : 1;
  }
  return compareGridDateStrings(v1, v2);
}

const dateColumnSort = {
  sortable: true as const,
  sortComparator: compareGridDateStringsPreferInboundPo,
};

interface Row {
  id: number;
  priority: number;
  rfqStatus: RFQStatus;
  rfqNum: string;
  customerName: string;
  buyerName: string;
  status: string;
  note: string | null;
  receivedDate: string;
  approvedDate: string;
  dueDate: string;
  assignedTo: string;
  assignedToId: number | null;
  priceQuoteStatus: WorkQueuePriceQuoteStatus;
  priceQuoteReviewId: number | null;
  purchaseOrderStatus: "" | "Received";
  purchaseOrderReviewId: number | null;
  hasInboundPo: boolean;
}

const columns: GridColDef<Row>[] = [
  {
    field: "priority",
    headerName: "",
    width: 36,
    minWidth: 36,
    maxWidth: 36,
    sortable: true,
    filterable: false,
    disableColumnMenu: true,
    align: "center",
    headerAlign: "center",
    cellClassName: "rfqs-priority-cell",
    headerClassName: "rfqs-priority-cell",
    renderCell: (params: GridRenderCellParams<Row>) => (
      <RfqPriorityCell
        rfqId={params.row.id}
        priority={params.row.priority}
        rfqStatus={params.row.rfqStatus}
      />
    ),
  },
  { field: "customerName", headerName: "Company", minWidth: 150, flex: 1 },
  { field: "rfqNum", headerName: "RFQ #", minWidth: 120 },
  { field: "buyerName", headerName: "Buyer", minWidth: 140, flex: 1 },
  { field: "dueDate", headerName: "RFQ Due Date", minWidth: 120, ...dateColumnSort },
  {
    field: "note",
    headerName: "Note",
    minWidth: 150,
    flex: 1,
    sortable: false,
    cellClassName: "rfqs-note-cell",
    renderCell: (params: GridRenderCellParams<Row>) => (
      <RfqNoteCell
        rfqId={params.row.id}
        rfqReferenceNum={params.row.rfqNum}
        note={params.row.note}
      />
    ),
  },
  {
    field: "approvedDate",
    headerName: "Approved Date",
    minWidth: 120,
    ...dateColumnSort,
  },
  {
    field: "receivedDate",
    headerName: "Received Date",
    minWidth: 120,
    ...dateColumnSort,
  },
  {
    field: "assignedTo",
    headerName: "Assigned To",
    minWidth: 170,
    flex: 1,
    cellClassName: "rfqs-assigned-cell",
    renderCell: (params: GridRenderCellParams<Row>) => (
      <RfqAssignedToCell
        rfqId={params.row.id}
        assignedToId={params.row.assignedToId}
        assignedToLabel={params.row.assignedTo}
      />
    ),
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 130,
    cellClassName: "rfqs-status-cell",
    renderCell: (params: GridRenderCellParams<Row>) => (
      <RfqStatusCell
        rfqId={params.row.id}
        status={params.row.rfqStatus}
        statusLabel={params.row.status}
      />
    ),
  },
  {
    field: "priceQuoteStatus",
    headerName: "Price Quote",
    minWidth: 120,
    sortable: true,
  },
  {
    field: "purchaseOrderStatus",
    headerName: "PO",
    minWidth: 100,
    sortable: true,
  },
];

function isActiveWorkQueueStatus(status: RFQStatus) {
  return status === RFQStatus.OPEN || status === RFQStatus.IN_PROGRESS;
}

/** Draft RFQs from SAM.gov (NEW + notice id) belong on Work Queue for review. */
function isSamGovDraftOnWorkQueue(quote: Rfq) {
  return (
    quote.status === RFQStatus.NEW && Boolean(quote.samGovNoticeId?.trim())
  );
}

function isArchivedWorkQueueStatus(status: RFQStatus) {
  return (
    status !== RFQStatus.NEW &&
    status !== RFQStatus.OPEN &&
    status !== RFQStatus.IN_PROGRESS
  );
}

const RfqsPage: React.FC = () => {
  const {
    requestForQuotes = [],
    customers = [],
    users = [],
    viewArchivedRfqs,
    setViewArchivedRfqs,
    rfqStatuses = [],
    setRfqs,
    setSnackbarErrorMsg,
  } = useContext(AppContext);
  const [createQuote, setCreateQuote] = useState(false);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearch = searchParams.get("q") ?? "";
  const [search, setSearch] = useState(urlSearch);
  /** `q` this page wrote itself; only outside navigations (global search) reset the box. */
  const writtenUrlSearchRef = useRef(urlSearch);
  useEffect(() => {
    if (urlSearch !== writtenUrlSearchRef.current) {
      writtenUrlSearchRef.current = urlSearch;
      setSearch(urlSearch);
    }
  }, [urlSearch]);
  /** Server matches (line / inbound PO #s, RFQs older than the loaded 90 days). */
  const [serverSearch, setServerSearch] = useState<{
    query: string;
    ids: Set<number>;
    extraRfqs: Rfq[];
  }>({ query: "", ids: new Set(), extraRfqs: [] });
  const requestForQuotesRef = useRef(requestForQuotes);
  requestForQuotesRef.current = requestForQuotes;
  const fetchedSearchRfqsRef = useRef(new Map<number, Rfq>());
  // setSearchParams changes identity with the URL; as an effect dep it would
  // cancel the search right after this page writes `q`.
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;
  useEffect(() => {
    const query = search.trim();
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      if (query !== writtenUrlSearchRef.current) {
        writtenUrlSearchRef.current = query;
        setSearchParamsRef.current(query ? { q: query } : {}, { replace: true });
      }
      if (query.length < 2) {
        setServerSearch({ query: "", ids: new Set(), extraRfqs: [] });
        return;
      }
      void (async () => {
        try {
          const results = await searchRfqs(query, {
            limit: SERVER_SEARCH_LIMIT,
            signal: controller.signal,
          });
          const loadedIds = new Set(
            requestForQuotesRef.current.map((rfq) => rfq.id)
          );
          const cache = fetchedSearchRfqsRef.current;
          const missingIds = results
            .map((result) => result.id)
            .filter((id) => !loadedIds.has(id))
            .slice(0, MAX_FETCHED_SEARCH_RFQS);
          await Promise.all(
            missingIds
              .filter((id) => !cache.has(id))
              .map(async (id) => {
                cache.set(id, await getQuote(id, { signal: controller.signal }));
              })
          );
          setServerSearch({
            query,
            ids: new Set(results.map((result) => result.id)),
            extraRfqs: missingIds.flatMap((id) => cache.get(id) ?? []),
          });
        } catch {
          // Cancelled by a newer query, or the server search failed; matching
          // against the loaded work queue still applies.
        }
      })();
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [search]);
  const [viewMode, setViewMode] = useState<"list" | "calendar">("list");
  const [dateSortField, setDateSortField] =
    useState<WorkQueueCalendarDateField>("receivedDate");
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "receivedDate", sort: "desc" },
  ]);
  const [priceQuotes, setPriceQuotes] = useState<PriceQuote[]>([]);
  const {
    purchaseOrders: allPurchaseOrders,
    inboundDraftPurchaseOrders: inboundPurchaseOrders,
  } = useWorkQueuePurchaseOrders();
  const navigate = useNavigate();
  const location = useLocation();
  const apiRef = useGridApiRef();
  const [lastRfqId, setLastRfqId] = useState(() => getLastWorkQueueRfqId());
  const scrollAnchorRef = useRef<string | null>(null);

  useEffect(() => {
    if (location.pathname === QUEUE_URL) {
      setLastRfqId(getLastWorkQueueRfqId());
    }
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    const loadSideData = () => {
      void getPriceQuotes({ workQueue: true })
        .then((list) => {
          if (cancelled) return;
          setPriceQuotes((prev) =>
            priceQuoteListFingerprint(prev) === priceQuoteListFingerprint(list)
              ? prev
              : list
          );
        })
        .catch(() => {
          /* keep last good list */
        });
    };
    loadSideData();
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      loadSideData();
    }, 60_000);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        loadSideData();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    const unsubscribe = subscribePriceQuotesChanged(loadSideData);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      unsubscribe();
    };
  }, []);

  const handleEditRfq = (rfqId: number) => {
    rememberLastWorkQueueRfq(rfqId);
    navigate(RFQ_URL.replace(":rfqId", rfqId.toString()), {
      state: { rfqReturnPath: QUEUE_URL },
    });
  };

  const handleMoveRfqDate = useCallback(
    async (rfqId: number, dateYmd: string) => {
      let previous: Rfq | undefined;
      setRfqs((prev) => {
        previous = prev.find((quote) => quote.id === rfqId);
        if (!previous) return prev;
        return prev.map((quote) =>
          quote.id !== rfqId
            ? quote
            : dateSortField === "dueDate"
              ? { ...quote, dueDate: dateYmd }
              : { ...quote, receivedDate: dateYmd }
        );
      });
      if (!previous) return;

      try {
        const updated = await updateRfq(
          rfqId,
          dateSortField === "dueDate"
            ? { dueDate: dateYmd }
            : { receivedDate: dateYmd }
        );
        setRfqs((prev) =>
          prev.map((quote) => (quote.id === updated.id ? updated : quote))
        );
      } catch (error) {
        const reverted = previous;
        setRfqs((prev) =>
          prev.map((quote) => (quote.id === rfqId ? reverted : quote))
        );
        setSnackbarErrorMsg(
          error instanceof Error
            ? error.message
            : dateSortField === "dueDate"
              ? "Failed to update due date"
              : "Failed to update received date"
        );
      }
    },
    [dateSortField, setRfqs, setSnackbarErrorMsg]
  );

  const handleWorkQueueRowClick = (row: Row) => {
    rememberLastWorkQueueRfq(row.id);
    // Pending inbound PO → comparison in a new tab (stay on work queue).
    if (
      row.hasInboundPo &&
      row.purchaseOrderReviewId != null &&
      row.purchaseOrderReviewId > 0
    ) {
      openPurchaseOrderReviewInNewTab(row.purchaseOrderReviewId);
      return;
    }
    // Received vendor price quote → comparison in a new tab, RFQ in this tab.
    if (
      row.priceQuoteStatus === "Received" &&
      row.priceQuoteReviewId != null &&
      row.priceQuoteReviewId > 0
    ) {
      openPriceQuoteReviewInNewTab(row.priceQuoteReviewId);
    }
    handleEditRfq(row.id);
  };

  const getCustomerName = (rfq: Rfq) => {
    const customer = customers.find((c) => c.id === rfq.customerId);
    return customer?.name || rfq.customerName || "";
  };

  const getBuyerName = (rfq: Rfq) => {
    // Session catalogs are directory-lite (no contacts); prefer stored buyerName.
    if (rfq.buyerName) return rfq.buyerName;
    if (!rfq.buyerId) return "";
    for (const c of customers) {
      const buyer = c.contacts?.find((contact) => contact.id === rfq.buyerId);
      if (buyer?.name) return buyer.name;
    }
    return "";
  };

  const getAssignedUserName = (rfq: Rfq) => {
    const user = users.find((u) => u.id === rfq.assignedToId);
    return user ? user.firstName + " " + user.lastName : "";
  };

  const getLatestStatus = (rfq: Rfq): string => {
    return getRFQStatusLabel(rfq.status, rfqStatuses);
  };

  const searchNeedle = search.trim().toLowerCase();
  const isSearchingRfqNum = searchNeedle !== "";

  const vendorOrderRfqIds = useMemo(
    () => vendorOrderRfqIdSet(requestForQuotes, allPurchaseOrders),
    [requestForQuotes, allPurchaseOrders]
  );

  const draftInboundPoByRfqId = useMemo(
    () =>
      latestDraftInboundPoByRfqId(requestForQuotes, inboundPurchaseOrders),
    [requestForQuotes, inboundPurchaseOrders]
  );

  const poNumsByRfqId = useMemo(
    () => poNumbersByRfqId(requestForQuotes, allPurchaseOrders),
    [requestForQuotes, allPurchaseOrders]
  );

  const serverMatches =
    serverSearch.query === search.trim() ? serverSearch : null;

  const displayedQuotes = useMemo(() => {
    const extraRfqs = isSearchingRfqNum ? serverMatches?.extraRfqs ?? [] : [];
    const loadedIds = new Set(requestForQuotes.map((quote) => quote.id));
    const pool = extraRfqs.length
      ? [
          ...requestForQuotes,
          ...extraRfqs.filter((quote) => !loadedIds.has(quote.id)),
        ]
      : requestForQuotes;
    return pool.filter((quote) => {
      if (isSearchingRfqNum) {
        const matchesRfqNum = (quote.referenceNum ?? "")
          .toLowerCase()
          .includes(searchNeedle);
        const matchesPoNum = (poNumsByRfqId.get(quote.id) ?? []).some((po) =>
          po.toLowerCase().includes(searchNeedle)
        );
        const matchesServer = serverMatches?.ids.has(quote.id) ?? false;
        if (!matchesRfqNum && !matchesPoNum && !matchesServer) return false;
      }
      const hasPendingPoComparison =
        draftInboundPoByRfqId.has(quote.id) &&
        !vendorOrderRfqIds.has(quote.id);
      const inStatusBucket = isSearchingRfqNum
        ? isActiveWorkQueueStatus(quote.status) ||
          isArchivedWorkQueueStatus(quote.status) ||
          isSamGovDraftOnWorkQueue(quote)
        : viewArchivedRfqs
          ? isArchivedWorkQueueStatus(quote.status)
          : isActiveWorkQueueStatus(quote.status) ||
            isSamGovDraftOnWorkQueue(quote) ||
            // Archived RFQs with a draft inbound PO stay on the active queue
            // (pinned to the top) until comparison is done.
            (isArchivedWorkQueueStatus(quote.status) &&
              hasPendingPoComparison);
      if (!inStatusBucket) return false;
      if (
        !isSearchingRfqNum &&
        !viewArchivedRfqs &&
        isActiveWorkQueueStatus(quote.status) &&
        vendorOrderRfqIds.has(quote.id)
      ) {
        return false;
      }
      return true;
    });
  }, [
    requestForQuotes,
    serverMatches,
    isSearchingRfqNum,
    searchNeedle,
    poNumsByRfqId,
    viewArchivedRfqs,
    vendorOrderRfqIds,
    draftInboundPoByRfqId,
  ]);

  const gridColumns = useMemo((): GridColDef<Row>[] => {
    const priceQuoteColumn: GridColDef<Row> = {
      field: "priceQuoteStatus",
      headerName: "Price Quote",
      minWidth: 120,
      sortable: true,
      renderCell: (params: GridRenderCellParams<Row>) => {
        const status = params.row.priceQuoteStatus;
        if (!status) return null;
        if (status === "Received" && params.row.priceQuoteReviewId != null) {
          return (
            <Tooltip title="Review price quote">
              <Chip
                size="small"
                color="success"
                label="Received"
                clickable
                onClick={(e) => {
                  e.stopPropagation();
                  rememberLastWorkQueueRfq(params.row.id);
                  navigate(
                    PRICE_QUOTE_REVIEW_URL.replace(
                      ":priceQuoteId",
                      String(params.row.priceQuoteReviewId)
                    ),
                    { state: { rfqReturnPath: QUEUE_URL } }
                  );
                }}
                sx={{ cursor: "pointer" }}
              />
            </Tooltip>
          );
        }
        if (status === "Awaiting") {
          return <Chip size="small" color="warning" label="Awaiting" />;
        }
        return null;
      },
    };

    const purchaseOrderColumn: GridColDef<Row> = {
      field: "purchaseOrderStatus",
      headerName: "PO",
      minWidth: 100,
      sortable: true,
      renderCell: (params: GridRenderCellParams<Row>) => {
        if (params.row.purchaseOrderStatus !== "Received") return null;
        if (params.row.purchaseOrderReviewId == null) {
          return <Chip size="small" color="success" label="Received" />;
        }
        return (
          <Tooltip title="Review purchase order">
            <Chip
              size="small"
              color="success"
              label="Received"
              clickable
              onClick={(e) => {
                e.stopPropagation();
                rememberLastWorkQueueRfq(params.row.id);
                navigate(
                  PO_REVIEW_URL.replace(
                    ":poId",
                    String(params.row.purchaseOrderReviewId)
                  ),
                  { state: { rfqReturnPath: QUEUE_URL } }
                );
              }}
              sx={{ cursor: "pointer" }}
            />
          </Tooltip>
        );
      },
    };

    const baseColumns = columns.map((col) => {
      if (col.field === "priceQuoteStatus") return priceQuoteColumn;
      if (col.field === "purchaseOrderStatus") return purchaseOrderColumn;
      return col;
    });

    if (viewArchivedRfqs) {
      return baseColumns;
    }

    const actionsColumn: GridColDef<Row> = {
      field: "actions",
      headerName: "",
      width: 74,
      flex: 0,
      minWidth: 74,
      maxWidth: 74,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: "center",
      headerAlign: "center",
      cellClassName: "rfqs-actions-cell",
      headerClassName: "rfqs-actions-cell",
      renderCell: (params: GridRenderCellParams<Row>) => {
        if (!isActiveWorkQueueStatus(params.row.rfqStatus)) {
          return null;
        }
        return (
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
              overflow: "visible",
            }}
          >
            <CloseRfqSplitButton
              rfqId={params.row.id}
              referenceNum={params.row.rfqNum}
              status={params.row.rfqStatus}
            />
          </Box>
        );
      },
    };

    return [...baseColumns, actionsColumn];
  }, [viewArchivedRfqs, navigate]);

  const rows: Row[] = useMemo(() => {
    return displayedQuotes
      .map((quote: Rfq) => {
        const latest = latestReceivedPriceQuote(quote.id, priceQuotes);
        const latestPo = draftInboundPoByRfqId.get(quote.id) ?? null;
        const purchaseOrderStatus: "" | "Received" = latestPo
          ? "Received"
          : "";
        return {
          id: quote.id,
          priority: quote.priority ?? 0,
          rfqStatus: quote.status,
          rfqNum: quote.referenceNum,
          customerName: getCustomerName(quote),
          buyerName: getBuyerName(quote),
          assignedTo: getAssignedUserName(quote),
          assignedToId: quote.assignedToId ?? null,
          status: getLatestStatus(quote),
          note: quote.note ?? null,
          // Prefer inbound email time; otherwise when the RFQ entered the system.
          receivedDate: DateTimeUtils.toString(
            quote.emailReceivedAt ?? quote.createdAt
          ),
          approvedDate: (() => {
            if (quote.approvedDate) {
              return DateTimeUtils.toString(quote.approvedDate);
            }
            const dates = (quote.quotes ?? [])
              .map((h) => h.approvalDate)
              .filter((d): d is Date => d != null)
              .map((d) => new Date(d).getTime());
            if (dates.length === 0) return "";
            return DateTimeUtils.toString(new Date(Math.max(...dates)));
          })(),
          dueDate: quote.dueDate ? DateTimeUtils.toString(quote.dueDate) : "",
          priceQuoteStatus: workQueuePriceQuoteStatus(quote, priceQuotes),
          priceQuoteReviewId: latest?.id ?? null,
          purchaseOrderStatus,
          purchaseOrderReviewId: latestPo?.id ?? null,
          hasInboundPo: purchaseOrderStatus === "Received",
        };
      })
      // Pending PO reviews float to the top of the work queue.
      .sort((a, b) => Number(b.hasInboundPo) - Number(a.hasInboundPo));
    // Name helpers close over customers/users/rfqStatuses — include them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    displayedQuotes,
    priceQuotes,
    draftInboundPoByRfqId,
    customers,
    users,
    rfqStatuses,
  ]);

  const calendarItems = useMemo(() => {
    if (viewMode !== "calendar") return [];
    return rows.map((row) => {
      const quote = displayedQuotes.find((q) => q.id === row.id);
      if (!quote?.receivedDate) return row;
      return {
        ...row,
        receivedDate: DateTimeUtils.toString(quote.receivedDate),
      };
    });
  }, [viewMode, rows, displayedQuotes]);

  const lastVisitedRowFingerprint = useMemo(() => {
    if (lastRfqId == null) {
      return "";
    }
    const row = rows.find((entry) => entry.id === lastRfqId);
    if (!row) {
      return `missing:${lastRfqId}`;
    }
    return [
      row.id,
      row.status,
      row.note ?? "",
      row.priority,
      row.assignedTo,
      row.hasInboundPo,
      row.priceQuoteStatus,
    ].join(":");
  }, [rows, lastRfqId]);

  useEffect(() => {
    setSortModel([{ field: dateSortField, sort: "desc" }]);
  }, [dateSortField]);

  useEffect(() => {
    if (viewMode !== "list" || lastRfqId == null || rows.length === 0) return;

    const scrollAnchor = `${location.pathname}:${lastRfqId}:${lastVisitedRowFingerprint}`;
    if (scrollAnchorRef.current === scrollAnchor) return;

    let cancelled = false;
    let attempts = 0;
    let timeoutId = 0;

    const tryScroll = () => {
      if (cancelled || scrollAnchorRef.current === scrollAnchor) return;
      const api = apiRef.current;
      const sortedIds = api?.getSortedRowIds?.() ?? [];
      if (!api || sortedIds.length === 0) {
        if (attempts++ < 10) {
          timeoutId = window.setTimeout(tryScroll, 50);
        }
        return;
      }
      const index = sortedIds.findIndex((id) => Number(id) === lastRfqId);
      if (index < 0) return;
      scrollAnchorRef.current = scrollAnchor;
      const pagination = api.state?.pagination?.paginationModel;
      const pageSize = pagination?.pageSize ?? 100;
      const page = Math.floor(index / pageSize);
      if (pagination && pagination.page !== page) {
        api.setPaginationModel({ ...pagination, page });
      }
      window.requestAnimationFrame(() => {
        if (cancelled) return;
        try {
          const rowIndex = api.getRowIndexRelativeToVisibleRows(lastRfqId);
          api.scrollToIndexes({ rowIndex });
        } catch {
          document
            .querySelector(`.MuiDataGrid-row[data-id="${lastRfqId}"]`)
            ?.scrollIntoView({ block: "center" });
        }
      });
    };

    timeoutId = window.setTimeout(tryScroll, 50);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    apiRef,
    lastRfqId,
    lastVisitedRowFingerprint,
    location.pathname,
    rows.length,
    viewMode,
  ]);

  const handleCloseEditQuoteDialog = (rfq?: Rfq, editMode?: boolean) => {
    setCreateQuote(false);
    if (editMode) {
      rememberLastWorkQueueRfq(rfq!.id);
      navigate(RFQ_URL.replace(":rfqId", rfq!.id.toString()), {
        state: { rfqReturnPath: QUEUE_URL },
      });
    }
  };

  return (
    <Stack pl={2} pr={2} direction="column" alignItems="stretch" gap={1} sx={{ minHeight: 0, flex: 1 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={1}
        flexWrap="wrap"
        sx={{ minWidth: 0 }}
      >
        <WorkQueueTabs />
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
          sx={{ flexShrink: 0, ml: "auto" }}
        >
          <WorkQueueViewToggle value={viewMode} onChange={setViewMode} />
          <WorkQueueDateSortToggle
            value={dateSortField}
            onChange={setDateSortField}
          />
          <TextField
            size="small"
            label="RFQ # / PO #"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 160 }}
            slotProps={{
              input: {
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear RFQ # / PO # search"
                      onClick={() => setSearch("")}
                      edge="end"
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              },
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={viewArchivedRfqs}
                onChange={(_, checked) => setViewArchivedRfqs(checked)}
                color="primary"
              />
            }
            label="View Archived"
          />
          <Fab
            size="small"
            onClick={() => setCreateQuote(true)}
            color="primary"
            sx={{ visibility: viewArchivedRfqs ? "hidden" : "visible" }}
          >
            <Add />
          </Fab>
        </Stack>
      </Stack>
      {viewMode === "calendar" ? (
        <WorkQueueCalendar
          items={calendarItems}
          dateField={dateSortField}
          statusCatalog={rfqStatuses}
          highlightedRfqId={lastRfqId}
          onOpenRfq={(rfqId) => {
            const row = rows.find((r) => r.id === rfqId);
            if (row) {
              handleWorkQueueRowClick(row);
            } else {
              handleEditRfq(rfqId);
            }
          }}
          onMoveRfq={handleMoveRfqDate}
        />
      ) : (
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          height: "calc(100vh - 160px)",
        }}
      >
      <DataGrid
        apiRef={apiRef}
        sx={{
          height: "100%",
          marginBottom: "10px",
          gridColumn: "span 2",
          borderColor: grey[400],
          backgroundColor: "white",
          "& .rfqs-actions-cell": {
            paddingLeft: 4,
            paddingRight: 4,
            overflow: "visible",
            display: "flex",
            alignItems: "center",
          },
          "& .rfqs-actions-cell .MuiDataGrid-cellContent": {
            overflow: "visible",
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          },
          "& .rfqs-priority-cell": {
            paddingLeft: 1,
            paddingRight: 1,
            overflow: "visible",
          },
          "& .rfqs-priority-cell .MuiDataGrid-cellContent": {
            overflow: "visible",
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          },
          "& .rfqs-note-cell": {
            minWidth: 0,
          },
          "& .rfqs-note-cell .MuiDataGrid-cellContent": {
            overflow: "hidden",
            minWidth: 0,
            width: "100%",
          },
          "& .rfq-row-has-po": {
            backgroundColor: green[50],
          },
          "& .rfq-row-has-po:hover": {
            backgroundColor: `${green[100]} !important`,
          },
          "& .rfq-row-has-price-quote": {
            backgroundColor: `${RECEIVED_PRICE_QUOTE_LINE_HIGHLIGHT.bgcolor} !important`,
            outline: RECEIVED_PRICE_QUOTE_LINE_HIGHLIGHT.outline,
            outlineOffset: -1,
          },
          "& .rfq-row-has-price-quote:hover": {
            backgroundColor: "rgba(255, 105, 180, 0.24) !important",
          },
          "& .rfq-row-last-visited": {
            backgroundColor: `${lightBlue[50]} !important`,
            boxShadow: `inset 4px 0 0 ${lightBlue[400]}`,
          },
          "& .rfq-row-last-visited:hover": {
            backgroundColor: `${lightBlue[100]} !important`,
          },
          "& .rfq-row-has-po.rfq-row-last-visited": {
            backgroundColor: `${green[50]} !important`,
            boxShadow: `inset 4px 0 0 ${lightBlue[400]}`,
          },
          "& .rfq-row-has-po.rfq-row-last-visited:hover": {
            backgroundColor: `${green[100]} !important`,
          },
        }}
        density="compact"
        rows={rows}
        disableRowSelectionOnClick
        columns={gridColumns}
        getRowClassName={(params) => {
          const classes: string[] = [];
          if (params.row.hasInboundPo) classes.push("rfq-row-has-po");
          if (params.row.priceQuoteStatus === "Received") {
            classes.push("rfq-row-has-price-quote");
          }
          if (lastRfqId != null && params.row.id === lastRfqId) {
            classes.push("rfq-row-last-visited");
          }
          return classes.join(" ");
        }}
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        onRowClick={(params) => handleWorkQueueRowClick(params.row as Row)}
      />
      </Box>
      )}
      {createQuote ? (
        <CreateRfqDialog onClose={handleCloseEditQuoteDialog} />
      ) : (
        <></>
      )}
    </Stack>
  );
};

export default RfqsPage;
